/**
 * Query (design board 4): one store and its language (SQL, Mongo, Cypher, Redis, Kafka), the query typed or written from
 * words ("Ask in words", where the sandbox has a model for it), Run (⌘↵), the result as a table, JSON or — for
 * Cypher — a small graph. Queries run read-only: one that changes data asks first, then runs with `allowWrite`.
 * Kafka's query is JSON (topic, where, since, …: KAFKA_QUERY_HELP) and only ever reads, by a bounded scan.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { ArrowLeft, BookmarkSimple, Export, Play, Sparkle, TreeStructure, Warning } from '@phosphor-icons/react';
import { DataDialog, EmptyState, ErrorState, ReadOnlyBadge, errorText, saveBlob } from './DataCommon';
import { PlanResult, SaveSnippetDialog, SnippetsCard } from './QueryExtras';
import {
  askQuery, deleteSnippet, explainQuery, listSnippets, runQuery, saveSnippet, updateSnippet,
} from '../../services/DataService';
import {
  ASK_PLACEHOLDERS, KAFKA_QUERY_HELP, LANGUAGES, columnNames, displayValue, formatCount, isQueryable, kindLabel, languageOf, queryWrites, sampleQuery, serviceLabel, storeLabel, toCsv,
} from '../../utils/dataModel';

// Once the server said it has no model for "Ask in words", the box stays off for this page load.
let askAvailable = true;
export const resetAskAvailability = () => { askAvailable = true; };

const WRITE_CODES = new Set(['read_only_query', 'write_requires_confirmation', 'writes_need_allow', 'would_write', 'read_only']);
const QUERY_LABELS = { sql: 'SQL query', mongo: 'Mongo query (JSON)', cypher: 'Cypher query', redis: 'Redis command', kafka: 'Kafka query (JSON)' };
const ASK_FALLBACK = 'Describe what you want to see';
// Languages with a plan (vfs-service src/data/routesMore.js EXPLAIN_LANGUAGES).
const EXPLAINS = new Set(['sql', 'mongo', 'cypher']);
const snippetName = (text) => String(text || '').split('\n').map((l) => l.trim()).find(Boolean)?.slice(0, 60) || 'Query';

/** " · 2,400 messages read" and, for Kafka, whether the scan stopped at its bound or its timeout. */
function scanNote(result) {
  if (result?.scanned === undefined) { return ''; }
  const read = ` · ${formatCount(result.scanned)} message${result.scanned === 1 ? '' : 's'} read`;
  if (result.partial) { return `${read} (stopped at the time limit: narrow with since, partition or fromOffset)`; }
  return result.scanLimited ? `${read} (the scan limit: older messages were not read; narrow with since, partition or fromOffset)` : read;
}

const cellOf = (row, columns, column) => (Array.isArray(row) ? row[columns.indexOf(column)] : row?.[column]);

/** The nodes and relationships in a Cypher result, for the graph view. */
function graphOf(result) {
  const nodes = new Map();
  const links = [];
  const visit = (value) => {
    if (!value || typeof value !== 'object') { return; }
    if (Array.isArray(value)) { value.forEach(visit); return; }
    const id = value.elementId ?? value.identity ?? value.id;
    if (Array.isArray(value.labels) && id !== undefined) {
      const props = value.properties || {};
      nodes.set(String(id), { id: String(id), label: String(props.name ?? props.sku ?? props.title ?? value.labels[0] ?? id) });
      return;
    }
    const start = value.startNodeElementId ?? value.start;
    const end = value.endNodeElementId ?? value.end;
    if (value.type && start !== undefined && end !== undefined) { links.push({ from: String(start), to: String(end), type: value.type }); return; }
    if (Array.isArray(value.segments)) { value.segments.forEach((s) => { visit(s.start); visit(s.relationship); visit(s.end); }); return; }
    Object.values(value).forEach(visit);
  };
  (result?.rows || []).forEach(visit);
  return { nodes: [...nodes.values()].slice(0, 60), links: links.filter((l) => nodes.has(l.from) && nodes.has(l.to)) };
}

function GraphResult({ result }) {
  const { nodes, links } = useMemo(() => graphOf(result), [result]);
  if (!nodes.length) { return <EmptyState title="No nodes in this result">Return nodes (RETURN n) or paths to see them as a graph.</EmptyState>; }
  const size = 320;
  const r = nodes.length === 1 ? 0 : size / 2 - 40;
  const at = Object.fromEntries(nodes.map((n, i) => [n.id, {
    x: size / 2 + r * Math.cos((2 * Math.PI * i) / nodes.length - Math.PI / 2),
    y: size / 2 + r * Math.sin((2 * Math.PI * i) / nodes.length - Math.PI / 2),
  }]));
  return (
    <svg aria-label={`Graph: ${nodes.length} nodes, ${links.length} relationships`} className="data-graph" role="img" viewBox={`0 0 ${size} ${size}`}>
      {links.map((l, i) => <line className="data-graph-link" key={i} x1={at[l.from].x} x2={at[l.to].x} y1={at[l.from].y} y2={at[l.to].y} />)}
      {nodes.map((n) => (
        <g key={n.id} transform={`translate(${at[n.id].x} ${at[n.id].y})`}>
          <circle className="data-graph-node" r={16} />
          <text className="data-graph-text" dy={30} textAnchor="middle">{n.label.length > 16 ? `${n.label.slice(0, 15)}…` : n.label}</text>
        </g>
      ))}
    </svg>
  );
}

GraphResult.propTypes = { result: PropTypes.object };

export default function QueryView({ projectId, stores = [], initialStore = null, initialQuery = null, focusAsk = false, readOnly = false, onBack, onApplied = null }) {
  const queryable = stores.filter(isQueryable);
  const start = queryable.find((s) => s.id === initialStore) || queryable.find((s) => s.status === 'running') || queryable[0] || null;
  const [storeId, setStoreId] = useState(start?.id || '');
  const store = queryable.find((s) => s.id === storeId) || null;
  const language = languageOf(store?.kind);
  const [text, setText] = useState(() => initialQuery || sampleQuery(start));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [view, setView] = useState('table');
  const [askText, setAskText] = useState('');
  const [ask, setAsk] = useState({ available: askAvailable, busy: false, explanation: null, error: null });
  const askRef = useRef(null);
  const editorRef = useRef(null);
  const [plan, setPlan] = useState(null);
  const [explaining, setExplaining] = useState(false);
  const [snippets, setSnippets] = useState([]);
  const [snippetsError, setSnippetsError] = useState(null);
  const [saving, setSaving] = useState(null); // { busy, error } while the dialog is open

  useEffect(() => { if (focusAsk && ask.available) { askRef.current?.focus(); } else { editorRef.current?.focus(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let live = true;
    Promise.resolve().then(() => listSnippets(projectId))
      .then((answer) => { if (live) { setSnippets(answer?.snippets || []); } })
      // An older sandbox without snippets: nothing to show, not an error.
      .catch((e) => { if (live && e?.code !== 'sandbox_outdated') { setSnippetsError(`Snippets: ${errorText(e)}`); } });
    return () => { live = false; };
  }, [projectId]);

  const chooseStore = (id) => {
    const next = queryable.find((s) => s.id === id);
    setStoreId(id);
    setResult(null);
    setError(null);
    if (next && languageOf(next.kind) !== language) { setText(sampleQuery(next)); }
    if (view === 'graph' && next?.kind !== 'neo4j') { setView('table'); }
  };
  const chooseLanguage = (id) => {
    const next = queryable.find((s) => languageOf(s.kind) === id && s.status === 'running') || queryable.find((s) => languageOf(s.kind) === id);
    if (next) { chooseStore(next.id); }
  };

  const execute = async (allowWrite, { on = store, query = text } = {}) => {
    if (!on || !query.trim()) { return; }
    setRunning(true);
    setError(null);
    setPlan(null);
    try {
      const answer = await runQuery(projectId, { store: on.id, language: languageOf(on.kind), query, allowWrite });
      setResult(answer);
      setConfirming(false);
      if (allowWrite && (answer?.restorePoint || answer?.restorePointWarning)) {
        onApplied?.({ restorePoint: answer.restorePoint || null, restorePointWarning: answer.restorePointWarning || null, summary: `Query run on ${storeLabel(on)}` });
      }
    } catch (e) {
      if (!allowWrite && !readOnly && !on.readOnly && WRITE_CODES.has(e?.code)) {
        setConfirming(true);
      } else {
        setError(errorText(e));
        setConfirming(false);
      }
    } finally {
      setRunning(false);
    }
  };

  const run = ({ on = store, query = text } = {}) => {
    if (!on || running) { return; }
    if (queryWrites(languageOf(on.kind), query)) {
      if (on.readOnly) { setError(`${kindLabel(on.kind)} is read-only here: this query would change data.`); return; }
      if (readOnly) { setError('You are a viewer of this project: you can run queries that read, not ones that change data.'); return; }
      setConfirming(true);
      return;
    }
    execute(false, { on, query });
  };

  const explain = async (analyze = false) => {
    if (!store || !text.trim() || !EXPLAINS.has(language)) { return; }
    setExplaining(true);
    setError(null);
    try {
      const answer = await explainQuery(projectId, { store: store.id, language, query: text, analyze });
      setPlan(answer);
      setResult(null);
    } catch (e) {
      setError(`Explain: ${errorText(e)}`);
    } finally {
      setExplaining(false);
    }
  };

  // A snippet goes into the editor on the store it was saved for (else the first of its language).
  const snippetStore = (snippet) => {
    const ofLanguage = queryable.filter((s) => languageOf(s.kind) === snippet.language);
    return ofLanguage.find((s) => s.id === snippet.store) || ofLanguage.find((s) => s.status === 'running') || ofLanguage[0] || null;
  };
  const loadSnippet = (snippet) => {
    const on = snippetStore(snippet);
    if (!on) { return null; }
    setStoreId(on.id);
    setText(snippet.query);
    setResult(null);
    setPlan(null);
    setError(null);
    if (view === 'graph' && on.kind !== 'neo4j') { setView('table'); }
    return on;
  };
  const runSnippet = (snippet) => {
    const on = loadSnippet(snippet);
    if (on) { run({ on, query: snippet.query }); }
  };
  const save = async (name) => {
    setSaving({ busy: true, error: null });
    try {
      const answer = await saveSnippet(projectId, { name, language, store: store?.id, query: text });
      if (answer?.snippet) { setSnippets((prev) => [...prev, answer.snippet]); }
      setSaving(null);
    } catch (e) {
      setSaving({ busy: false, error: errorText(e) });
    }
  };
  const rename = async (snippet, name) => {
    const answer = await updateSnippet(projectId, snippet.id, { name });
    setSnippets((prev) => prev.map((s) => (s.id === snippet.id ? answer?.snippet || { ...s, name } : s)));
  };
  const remove = async (snippet) => {
    await deleteSnippet(projectId, snippet.id);
    setSnippets((prev) => prev.filter((s) => s.id !== snippet.id));
  };

  const writeWords = async () => {
    if (!askText.trim() || !store) { return; }
    setAsk((prev) => ({ ...prev, busy: true, error: null, explanation: null }));
    try {
      const answer = await askQuery(projectId, { store: store.id, language, prompt: askText.trim() });
      if (answer?.query) { setText(answer.query); }
      setAsk({ available: true, busy: false, explanation: answer?.explanation || null, error: null });
      editorRef.current?.focus();
    } catch (e) {
      if (e?.status === 501 || e?.code === 'not_available') {
        askAvailable = false;
        setAsk({ available: false, busy: false, explanation: null, error: null });
      } else {
        setAsk((prev) => ({ ...prev, busy: false, error: errorText(e) }));
      }
    }
  };

  const columns = columnNames(result);
  const rows = result?.rows || [];
  const services = (s) => (s.services?.length ? ` (${s.services.map(serviceLabel).join(', ')})` : '');

  if (!queryable.length) {
    return (
      <div className="data-view" data-testid="query-view">
        <div className="data-view-head">
          <button className="data-btn is-ghost" onClick={onBack} type="button"><ArrowLeft aria-hidden size={15} />Data</button>
          <h1>Query</h1>
        </div>
        <EmptyState title="Nothing to query yet">A database your services use (PostgreSQL, SQLite, MongoDB, Neo4j, Redis or Kafka) shows up here.</EmptyState>
      </div>
    );
  }

  return (
    <div className="data-view" data-testid="query-view">
      <div className="data-view-head">
        <button className="data-btn is-ghost" onClick={onBack} type="button"><ArrowLeft aria-hidden size={15} />Data</button>
        <h1>Query</h1>
        <label className="data-sr" htmlFor="data-query-store">Datastore</label>
        <select className="data-input" id="data-query-store" onChange={(e) => chooseStore(e.target.value)} value={storeId}>
          {queryable.map((s) => (
            <option key={s.id} value={s.id}>{storeLabel(s)}{services(s)}{s.status === 'stopped' ? ' · stopped' : ''}</option>
          ))}
        </select>
        <div aria-label="Language" className="data-seg" role="group">
          {LANGUAGES.map((l) => (
            <button
              aria-pressed={language === l.id}
              disabled={!queryable.some((s) => l.kinds.includes(s.kind))}
              key={l.id}
              onClick={() => chooseLanguage(l.id)}
              type="button"
            >
              {l.label}
            </button>
          ))}
        </div>
        <span className="data-grow" />
        {readOnly || store?.readOnly
          ? <ReadOnlyBadge label="Read-only" reason={readOnly ? 'You are a viewer of this project: queries only read' : `${kindLabel(store.kind)} is read-only here`} />
          : <span className="data-pill" data-testid="data-readonly" title="Queries run read-only; one that changes data asks you first">Read-only · a write asks first</span>}
      </div>

      {store?.status === 'stopped' && (
        <p className="data-banner" role="status"><Warning aria-hidden size={15} />{storeLabel(store)} is not running: start a service that uses it, then run the query.</p>
      )}

      <div className="data-card data-query-box">
        <div className="data-row-gap is-wrap">
          <label className="data-label" htmlFor="data-ask">Ask in words</label>
          <input
            className="data-input is-ai is-wide"
            disabled={!ask.available || ask.busy}
            id="data-ask"
            onChange={(e) => setAskText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); writeWords(); } }}
            placeholder={ask.available ? ASK_PLACEHOLDERS[language] || ASK_FALLBACK : 'Not available in this sandbox'}
            ref={askRef}
            value={askText}
          />
          <button className="data-btn is-ai" disabled={!ask.available || ask.busy || !askText.trim()} onClick={writeWords} type="button">
            <Sparkle aria-hidden size={15} />{ask.busy ? 'Writing…' : 'Write the query'}
          </button>
        </div>
        {!ask.available && <p className="data-footnote" data-testid="ask-unavailable">Asking in words is not available in this sandbox yet: write the query below.</p>}
        {ask.explanation && <p className="data-footnote">{ask.explanation}</p>}
        {ask.error && <p className="data-error-text" role="alert">{ask.error}</p>}
        <label className="data-sr" htmlFor="data-query-text">{QUERY_LABELS[language] || 'Query'}</label>
        <textarea
          className="data-textarea data-mono"
          id="data-query-text"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); run(); } }}
          ref={editorRef}
          rows={6}
          spellCheck={false}
          value={text}
        />
        {language === 'kafka' && <p className="data-footnote data-mono" data-testid="kafka-query-help">{KAFKA_QUERY_HELP}</p>}
        <div className="data-row-gap is-wrap">
          <button aria-keyshortcuts="Meta+Enter Control+Enter" className="data-btn is-primary" disabled={running || !store || !text.trim()} onClick={() => run()} type="button">
            <Play aria-hidden size={14} />{running ? 'Running…' : 'Run'}<kbd className="data-kbd">⌘↵</kbd>
          </button>
          <button
            className="data-btn"
            data-testid="query-explain"
            disabled={explaining || !store || !text.trim() || !EXPLAINS.has(language)}
            onClick={() => explain(false)}
            title={EXPLAINS.has(language) ? 'How the database would run it: the plan, without running it' : `${QUERY_LABELS[language] || 'This query'} has no plan`}
            type="button"
          >
            <TreeStructure aria-hidden size={15} />{explaining ? 'Explaining…' : 'Explain'}
          </button>
          {!readOnly && (
            <button className="data-btn" data-testid="query-save-snippet" disabled={!text.trim() || !store} onClick={() => setSaving({ busy: false, error: null })} type="button">
              <BookmarkSimple aria-hidden size={15} />Save as snippet
            </button>
          )}
          <span className="data-grow" />
          {result && (
            <span className="data-muted" data-testid="query-stats">
              {formatCount(result.rowCount ?? rows.length)} row{(result.rowCount ?? rows.length) === 1 ? '' : 's'}
              {result.ms !== undefined ? ` · ${formatCount(result.ms)} ms` : ''}
              {result.truncated ? (result.limit ? ` · more match: limit ${formatCount(result.limit)}` : ' · limited to 1,000 rows') : ''}
              {scanNote(result)}
              {result.wrote ? ' · changed data' : ''}
            </span>
          )}
        </div>
      </div>

      <SnippetsCard
        canEdit={!readOnly}
        canRun={(snippet) => Boolean(snippetStore(snippet))}
        error={snippetsError}
        onDelete={remove}
        onLoad={loadSnippet}
        onRename={rename}
        onRun={runSnippet}
        snippets={snippets}
      />

      {error && <ErrorState error={error} />}
      {plan && <PlanResult canMeasure={store?.kind === 'postgres'} measuring={explaining} onMeasure={() => explain(true)} plan={plan} />}
      {result && (
        <div className="data-card data-result">
          <div className="data-card-head">
            <div aria-label="Result view" className="data-seg" role="group">
              <button aria-pressed={view === 'table'} onClick={() => setView('table')} type="button">Table</button>
              {store?.kind === 'neo4j' && <button aria-pressed={view === 'graph'} onClick={() => setView('graph')} type="button">Graph</button>}
              <button aria-pressed={view === 'json'} onClick={() => setView('json')} type="button">JSON</button>
            </div>
            {result.wrote && <span className="data-pill is-warn">Changed data</span>}
            <span className="data-grow" />
            <button className="data-btn" disabled={!rows.length} onClick={() => saveBlob(new Blob([toCsv(columns, rows)], { type: 'text/csv' }), 'query.csv')} type="button">
              <Export aria-hidden size={15} />Export CSV
            </button>
          </div>
          {view === 'table' && (rows.length ? (
            <div className="data-table-wrap">
              {/* The result as the query returned it; a header sorts it here, in the browser (ORDER BY sorts in the query). */}
              <DataTable
                className="data-table"
                columns={columns.map((c) => ({
                  key: c,
                  label: c,
                  minWidth: 140,
                  value: (row) => { const value = cellOf(row, columns, c); return value !== null && typeof value === 'object' ? displayValue(value) : value; },
                  title: (row) => displayValue(cellOf(row, columns, c)),
                  render: (row) => displayValue(cellOf(row, columns, c)),
                  cellClassName: (row) => { const value = cellOf(row, columns, c); return `${typeof value === 'number' ? 'data-mono' : ''}${value === null || value === undefined ? ' is-null' : ''}`.trim(); },
                }))}
                rowKey={(row, i) => i}
                rows={rows}
                testId="query-result"
              />
            </div>
          ) : <EmptyState title={result.wrote ? 'Done: the query changed data' : 'No rows'} />)}
          {view === 'json' && <pre className="data-text-preview data-mono" data-testid="query-json">{JSON.stringify(rows, null, 2)}</pre>}
          {view === 'graph' && <GraphResult result={result} />}
        </div>
      )}

      {saving && (
        <SaveSnippetDialog busy={saving.busy} error={saving.error} initialName={snippetName(text)} onClose={() => setSaving(null)} onSave={save} />
      )}

      {confirming && store && (
        <DataDialog
          footer={(
            <>
              <span className="data-grow" />
              <button className="data-btn" onClick={() => setConfirming(false)} type="button">Back to the query</button>
              <button className="data-btn is-primary" data-testid="confirm-write" disabled={running} onClick={() => execute(true)} type="button">
                {running ? 'Running…' : 'Run and change data'}
              </button>
            </>
          )}
          onClose={() => setConfirming(false)}
          subtitle={<>{kindLabel(store.kind)} in your sandbox{store.services?.length ? <> · used by <strong>{store.services.map(serviceLabel).join(', ')}</strong></> : null}</>}
          testId="query-write-dialog"
          title={`This query changes data in ${storeLabel(store)}`}
          wide
        >
          <ol aria-label="Statements" className="data-statements">
            <li className="data-statement"><span className="data-tag is-update">WRITE</span><pre>{text}</pre></li>
          </ol>
          <ul className="data-notes">
            <li><span className="data-dot is-orange" />It runs as written, on the data your services use right now.</li>
            <li><span className="data-dot is-blue" />It is recorded in Activity. A query&apos;s changes cannot be undone from here: edit rows in the table to keep Undo.</li>
          </ul>
          {error && <p className="data-error-text" role="alert">{error}</p>}
        </DataDialog>
      )}
    </div>
  );
}

QueryView.propTypes = {
  onApplied: PropTypes.func,
  focusAsk: PropTypes.bool,
  initialQuery: PropTypes.string,
  initialStore: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  stores: PropTypes.array,
};
